import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ClipboardList, PlusCircle, LogOut,
  BarChart3, Database, Menu, ChevronLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { path: "/",              label: "Dashboard",     icon: LayoutDashboard, testId: "nav-dashboard" },
  { path: "/tickets",       label: "All Tickets",   icon: ClipboardList,   testId: "nav-tickets" },
  { path: "/tickets/new",   label: "New Ticket",    icon: PlusCircle,      testId: "nav-new-ticket" },
  { path: "/analytics",     label: "Analytics",     icon: BarChart3,       testId: "nav-analytics" },
  { path: "/data-migration",label: "Data Migration",icon: Database,        testId: "nav-data-migration" },
];

const LOGO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_charger-service-hub-1/artifacts/1jv6mr6c_2.jpeg";

export function Sidebar({ open = true, onToggle }) {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <>
      {/* ── Sidebar panel ── */}
      <aside
        className={cn(
          "bg-gray-900 flex flex-col h-screen flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out",
          open ? "w-64" : "w-0"
        )}
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* Inner wrapper keeps content at full width so it clips cleanly */}
        <div className="w-64 flex flex-col h-full">
          {/* Brand + close button */}
          <div className="px-4 py-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm p-0.5">
                <img
                  src={LOGO}
                  alt="XSTRONG POWER"
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <div className="min-w-0">
                <h1
                  className="font-black text-white text-[11px] tracking-widest uppercase leading-tight truncate"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  XSTRONG POWER
                </h1>
                <p className="text-lime-400 text-[11px] font-semibold mt-0.5">Service Centre</p>
              </div>
            </div>

            {/* Close / collapse button */}
            <button
              onClick={onToggle}
              data-testid="sidebar-close-btn"
              title="Collapse sidebar"
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-800 hover:text-gray-200 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {navItems.map(({ path, label, icon: Icon, testId }) => {
              const isActive =
                path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(path) && path !== "/";
              return (
                <Link
                  key={path}
                  to={path}
                  data-testid={testId}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative",
                    isActive
                      ? "bg-lime-500/15 text-lime-400"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  )}
                >
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-lime-500 rounded-r-full" />
                  )}
                  <Icon className="w-4 h-4 flex-shrink-0 ml-0.5" />
                  <span className="truncate">{label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User + Logout */}
          <div className="p-3 border-t border-gray-800 flex-shrink-0">
            {user && (
              <div className="px-3 py-2 mb-1 rounded-xl bg-gray-800/60">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  Signed in
                </p>
                <p className="text-xs text-gray-300 truncate mt-0.5">{user.email}</p>
              </div>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-red-900/30 hover:text-red-400 transition-all duration-200 w-full"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Floating open button (visible only when sidebar is hidden) ── */}
      <button
        onClick={onToggle}
        data-testid="sidebar-open-btn"
        title="Open sidebar"
        className={cn(
          "fixed top-3.5 left-3 z-50 w-9 h-9 bg-gray-900 hover:bg-gray-800 text-white rounded-xl shadow-lg flex items-center justify-center transition-all duration-300",
          open ? "opacity-0 pointer-events-none scale-75" : "opacity-100 pointer-events-auto scale-100"
        )}
      >
        <Menu className="w-4 h-4" />
      </button>
    </>
  );
}
